'use client';

import type { ProjectProduct } from '@/api/generated/model/projectProduct';
import { badgeVariants } from '@/comps/primitives/badge';
import { QuestionMarkToolTip } from '@/comps/tooltip-button';
import { PROJECT_PRODUCT_OPTIONS } from '@/domains/dashboard/components/project-product.config';
import { jetbrainsMono } from '@/theme/utils/font.utils';
import { cn } from '@/utils/shad';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

export { DEFAULT_CREATE_PROJECT_PRODUCT } from '@/domains/dashboard/components/project-product.config';

export const CREATE_PROJECT_FIELD_LABEL_CLASSNAME = cn(
  jetbrainsMono.className,
  'text-[11px] tracking-[0.5px] text-zinc-500 uppercase',
);

type CreateProjectFieldLabelProps = {
  children: ReactNode;
};

export function CreateProjectFieldLabel({ children }: CreateProjectFieldLabelProps) {
  return <span className={CREATE_PROJECT_FIELD_LABEL_CLASSNAME}>{children}</span>;
}

const PROJECT_PRODUCT_TOOLTIP = (
  <div className="flex flex-col gap-2 text-left">
    <p>
      When creating a new project, select the mode that matches your use case to access products and features tailored
      to it.
    </p>
    <div>
      <p className="font-semibold text-white">Employee Agents</p>
      <p>
        Employee Agents help organizations understand and improve how their workforce uses AI tools. Nebuly shows how
        employees interact with AI, where adoption is working, and whether AI usage is translating into real value for
        the organization.
      </p>
    </div>
    <div>
      <p className="font-semibold text-white">Customer Agents</p>
      <p>
        Customer Agents are AI agents that interact directly with your customers, either inside your product or through
        external channels. Examples include customer support agents, in-app advisors, AI assistants, and other
        customer-facing experiences.
      </p>
    </div>
  </div>
);

type ProjectProductSelectorProps = {
  value: ProjectProduct;
  onChange: (value: ProjectProduct) => void;
  className?: string;
};

export function ProjectProductSelector({ value, onChange, className }: ProjectProductSelectorProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        Which agent does this project track?
        <QuestionMarkToolTip
          content={PROJECT_PRODUCT_TOOLTIP}
          kind="info"
          side="right"
          tooltipClassName="max-h-none max-w-xs whitespace-normal wrap-break-word"
          className="size-3.5 shrink-0 hover:text-white"
        />
      </span>

      <div className="flex flex-col gap-3">
        {PROJECT_PRODUCT_OPTIONS.map((option) => {
          const isSelected = value === option.value;
          const Icon = option.icon;

          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onChange(option.value)}
              className={cn(
                'flex w-full cursor-pointer flex-col gap-2 rounded-md border bg-neutral-950/40 p-3 text-left transition-all',
                isSelected ? 'border-white' : 'border-muted hover:border-muted-foreground',
              )}
            >
              <span className="flex w-full min-w-0 items-center gap-2">
                <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-muted bg-neutral-950/40 text-muted-foreground">
                  <Icon className="size-4" strokeWidth={1.8} aria-hidden />
                </span>
                <span className="min-w-0 truncate text-xs font-semibold text-white">{option.title}</span>
                {isSelected ? (
                  <Check className="ml-auto size-3.5 shrink-0 text-muted-foreground" strokeWidth={2.4} aria-hidden />
                ) : null}
              </span>

              <span className="block text-xs leading-normal text-pretty text-muted-foreground">
                {option.description}
              </span>

              <span className="flex flex-wrap items-center gap-1.5">
                {option.features.map((feature) => {
                  const FeatureIcon = feature.icon;

                  return (
                    <span
                      key={feature.label}
                      className={cn(
                        badgeVariants({ variant: 'outline', pill: true }),
                        'gap-1.5 font-normal text-muted-foreground',
                      )}
                    >
                      <FeatureIcon className="size-3 shrink-0" strokeWidth={1.8} aria-hidden />
                      {feature.label}
                    </span>
                  );
                })}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
